import React, { useState, useEffect } from "react";
import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  Button,
  alpha,
  Paper,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Pagination,
  Skeleton,
  Breadcrumbs,
  Link,
  Tooltip,
  Zoom,
  Fade,
  Avatar,
  AvatarGroup,
  Divider,
  Card,
  CardContent,
  CardActions,
  LinearProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import HomeIcon from '@mui/icons-material/Home';
import CheckIcon from '@mui/icons-material/Check';
import DiamondIcon from '@mui/icons-material/Diamond';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScienceIcon from '@mui/icons-material/Science';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TokenIcon from '@mui/icons-material/Token';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import CloseIcon from '@mui/icons-material/Close';
import { useInView } from 'react-intersection-observer';

// Styled Components
const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  borderBottom: `1px solid ${alpha('#9E9E9E', 0.15)}`,
  '& .MuiTab-root': {
    color: '#616161',
    fontSize: '0.95rem',
    fontWeight: 500,
    textTransform: 'none',
    minWidth: 'auto',
    padding: '12px 24px',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
}));

const SyntheticCard = styled(Card)(({ theme, ispremium }) => ({
  backgroundColor: '#FFFFFF',
  border: `1px solid ${ispremium ? theme.palette.primary.main : alpha('#9E9E9E', 0.15)}`,
  borderRadius: 0,
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px -8px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const PremiumBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  right: 20,
  backgroundColor: theme.palette.primary.main,
  color: '#000000',
  padding: '4px 12px',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: -10,
    width: 0,
    height: 0,
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderRight: `10px solid ${theme.palette.primary.main}`,
  },
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  borderRight: `1px solid ${alpha('#9E9E9E', 0.15)}`,
  '&:last-child': {
    borderRight: 'none',
  },
}));

// Mock Data for Synthetic Coins
const syntheticCoins = [
  {
    id: 1,
    name: 'Gold Synthetic Sovereign',
    symbol: 'GSS',
    price: 2450.75,
    change: 2.4,
    volume: '1.2M',
    marketCap: '24.5M',
    holders: 1254,
    isPremium: true,
    category: 'precious',
    features: ['Backed by Physical Gold', 'Audited', 'Insured'],
  },
  {
    id: 2,
    name: 'Silver Synthetic Eagle',
    symbol: 'SSE',
    price: 320.50,
    change: -0.8,
    volume: '850K',
    marketCap: '8.2M',
    holders: 892,
    isPremium: false,
    category: 'precious',
    features: ['Silver Backed', 'Verified', 'Liquid'],
  },
  {
    id: 3,
    name: 'Platinum Synthetic Coin',
    symbol: 'PSC',
    price: 987.25,
    change: 1.2,
    volume: '420K',
    marketCap: '4.8M',
    holders: 567,
    isPremium: true,
    category: 'precious',
    features: ['Platinum Backed', 'Rare', 'Premium'],
  },
  {
    id: 4,
    name: 'Palladium Synthetic Coin',
    symbol: 'PDC',
    price: 1850.00,
    change: 3.1,
    volume: '310K',
    marketCap: '3.9M',
    holders: 423,
    isPremium: false,
    category: 'precious',
    features: ['Palladium Backed', 'Industrial', 'Scarce'],
  },
  {
    id: 5,
    name: 'Diamond Synthetic Token',
    symbol: 'DST',
    price: 12500.00,
    change: 5.2,
    volume: '180K',
    marketCap: '15.2M',
    holders: 789,
    isPremium: true,
    category: 'gemstone',
    features: ['Diamond Backed', 'Certified', 'Investment Grade'],
  },
  {
    id: 6,
    name: 'Rare Metal Composite',
    symbol: 'RMC',
    price: 675.30,
    change: 0.5,
    volume: '560K',
    marketCap: '6.7M',
    holders: 634,
    isPremium: false,
    category: 'composite',
    features: ['Multi-Metal', 'Diversified', 'Stable'],
  },
  {
    id: 7,
    name: 'Historic Sovereign Token',
    symbol: 'HST',
    price: 3450.00,
    change: -1.3,
    volume: '95K',
    marketCap: '2.3M',
    holders: 287,
    isPremium: true,
    category: 'historic',
    features: ['Historic Backing', 'Collectible', 'Limited'],
  },
  {
    id: 8,
    name: 'Modern Bullion Token',
    symbol: 'MBT',
    price: 2100.00,
    change: 1.8,
    volume: '780K',
    marketCap: '9.1M',
    holders: 1023,
    isPremium: false,
    category: 'bullion',
    features: ['Gold Bullion', 'Modern', 'Liquid'],
  },
];

const SyntheticCoins = () => {
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('marketCap');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [displayedCoins, setDisplayedCoins] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    premium: 0,
    marketCap: 0,
    holders: 0,
  });
  
  const itemsPerPage = 6;
  const { ref: loadMoreRef, inView } = useInView();

  // Mock data for categories
  const categories = [
    { label: 'All Synthetic', value: 'all', count: 156 },
    { label: 'Precious Metals', value: 'precious', count: 42 },
    { label: 'Gemstones', value: 'gemstone', count: 28 },
    { label: 'Historic', value: 'historic', count: 86 },
    { label: 'Bullion', value: 'bullion', count: 64 },
    { label: 'Composite', value: 'composite', count: 52 },
  ];

  const sortOptions = [
    { value: 'marketCap', label: 'Market Cap' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'gainers', label: 'Top Gainers' },
    { value: 'holders', label: 'Most Holders' },
    { value: 'volume', label: 'Highest Volume' },
  ];

  const filterOptions = [
    { label: 'Premium Only', value: 'premium', icon: <WorkspacePremiumIcon /> },
    { label: 'High Volume', value: 'highvolume', icon: <TrendingUpIcon /> },
    { label: 'New Listings', value: 'new', icon: <ScienceIcon /> },
    { label: 'Verified Only', value: 'verified', icon: <CheckIcon /> },
  ];

  useEffect(() => {
    // Calculate stats
    const total = syntheticCoins.length;
    const premium = syntheticCoins.filter(c => c.isPremium).length;
    const marketCap = syntheticCoins.reduce((acc, coin) => acc + (coin.marketCap ? parseFloat(coin.marketCap.replace('M', '')) : 0), 0);
    const holders = syntheticCoins.reduce((acc, coin) => acc + coin.holders, 0);
    
    setStats({
      total,
      premium,
      marketCap: marketCap.toFixed(1) + 'M',
      holders: holders.toLocaleString(),
    });
  }, []);

  useEffect(() => {
    // Simulate loading more coins
    if (inView && !loading && displayedCoins.length < syntheticCoins.length) {
      setLoading(true);
      setTimeout(() => {
        const start = displayedCoins.length;
        const end = Math.min(start + itemsPerPage, syntheticCoins.length);
        setDisplayedCoins(prev => [...prev, ...syntheticCoins.slice(start, end)]);
        setLoading(false);
      }, 1000);
    }
  }, [inView, loading, displayedCoins.length]);

  const handleCategoryChange = (event, newValue) => {
    setCategory(newValue);
    setPage(1);
  };

  const handleFilterClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleFilterSelect = (filter) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
    setSearch('');
    setSortBy('marketCap');
    setCategory('all');
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFilteredCoins = () => {
    let filtered = [...syntheticCoins];

    // Apply category filter
    if (category !== 'all') {
      filtered = filtered.filter(coin => coin.category === category);
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(coin => 
        coin.name?.toLowerCase().includes(searchLower) ||
        coin.symbol?.toLowerCase().includes(searchLower)
      );
    }

    // Apply active filters
    activeFilters.forEach(filter => {
      switch(filter) {
        case 'premium':
          filtered = filtered.filter(coin => coin.isPremium);
          break;
        case 'highvolume':
          filtered = filtered.filter(coin => {
            const volume = parseFloat(coin.volume.replace('K', '').replace('M', ''));
            return volume > 500;
          });
          break;
        case 'verified':
          filtered = filtered.filter(coin => coin.features.includes('Audited') || coin.features.includes('Verified'));
          break;
        default:
          break;
      }
    });

    // Apply sorting
    switch(sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'gainers':
        filtered.sort((a, b) => b.change - a.change);
        break;
      case 'holders':
        filtered.sort((a, b) => b.holders - a.holders);
        break;
      case 'volume':
        filtered.sort((a, b) => {
          const volA = parseFloat(a.volume.replace('K', '').replace('M', ''));
          const volB = parseFloat(b.volume.replace('K', '').replace('M', ''));
          return volB - volA;
        });
        break;
      default:
        // Market Cap
        filtered.sort((a, b) => {
          const capA = parseFloat(a.marketCap.replace('M', ''));
          const capB = parseFloat(b.marketCap.replace('M', ''));
          return capB - capA;
        });
        break;
    }

    return filtered;
  };

  const filteredCoins = getFilteredCoins();
  const paginatedCoins = filteredCoins.slice(0, page * itemsPerPage);
  const totalPages = Math.ceil(filteredCoins.length / itemsPerPage);

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          sx={{ mb: 4, color: '#9E9E9E' }}
          separator={<Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9E9E9E' }} />}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              color: '#9E9E9E',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <HomeIcon sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Typography color="#2D2D2D">Synthetic Coins</Typography>
        </Breadcrumbs>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                mb: 2,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <PrecisionManufacturingIcon />
              Digital Assets
            </Typography>
            
            <Typography variant="h2" sx={{ color: '#2D2D2D', mb: 3 }}>
              Synthetic
              <Box component="span" sx={{ color: 'primary.main', mx: 2 }}>
               Diamond Coins
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              color="#616161" 
              sx={{ maxWidth: '700px', mx: 'auto' }}
            >
              Blockchain-backed digital representations of physical precious metals and rare coins. 
              Trade with the security of physical backing and the efficiency of digital assets.
            </Typography>
          </Box>

          {/* Stats Banner */}
          <Paper
            sx={{
              p: 3,
              mb: 6,
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: alpha('#9E9E9E', 0.15),
              boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
            }}
          >
            <Grid container spacing={3}>
              {[
                { label: 'Total Assets', value: stats.total, icon: <TokenIcon /> },
                { label: 'Premium Assets', value: stats.premium, icon: <WorkspacePremiumIcon /> },
                { label: 'Market Cap', value: '$' + stats.marketCap, icon: <ShowChartIcon /> },
                { label: 'Total Holders', value: stats.holders, icon: <AccountBalanceIcon /> },
              ].map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ color: 'primary.main', mb: 1 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="#616161">
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </motion.div>

        {/* Search and Filters Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 6,
            backgroundColor: '#FFFFFF',
            border: '1px solid',
            borderColor: alpha('#9E9E9E', 0.15),
            boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by name or symbol..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <CloseIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}>
                {/* Filter Button */}
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterClick}
                  sx={{
                    borderColor: alpha('#9E9E9E', 0.3),
                    color: '#616161',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  Filters
                  {activeFilters.length > 0 && (
                    <Badge
                      badgeContent={activeFilters.length}
                      sx={{
                        ml: 1,
                        '& .MuiBadge-badge': {
                          backgroundColor: 'primary.main',
                          color: '#FFFFFF',
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  )}
                </Button>

                {/* Sort Select */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                    startAdornment={<SortIcon sx={{ color: '#9E9E9E', mr: 1 }} />}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Categories Tabs */}
        <StyledTabs
          value={category}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {categories.map((cat) => (
            <Tab 
              key={cat.value} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{cat.label}</span>
                  <Chip
                    label={cat.count}
                    size="small"
                    sx={{
                      backgroundColor: cat.value === category ? '#FFFFFF' : alpha('#9E9E9E', 0.1),
                      color: cat.value === category ? 'primary.main' : '#616161',
                      fontSize: '0.75rem',
                      height: 20,
                    }}
                  />
                </Box>
              } 
              value={cat.value} 
            />
          ))}
        </StyledTabs>

        {/* Active Filters */}
        <AnimatePresence>
          {(activeFilters.length > 0 || search || category !== 'all') && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="#616161">
                  Active Filters:
                </Typography>
                
                {category !== 'all' && (
                  <Chip
                    label={`Category: ${categories.find(c => c.value === category)?.label}`}
                    onDelete={() => setCategory('all')}
                    sx={{
                      backgroundColor: alpha('#C6A75E', 0.1),
                      color: 'primary.main',
                      borderColor: 'primary.main',
                    }}
                  />
                )}
                
                {search && (
                  <Chip
                    label={`Search: ${search}`}
                    onDelete={() => setSearch('')}
                    sx={{
                      backgroundColor: alpha('#C6A75E', 0.1),
                      color: 'primary.main',
                      borderColor: 'primary.main',
                    }}
                  />
                )}
                
                {activeFilters.map(filter => (
                  <Chip
                    key={filter}
                    label={filterOptions.find(f => f.value === filter)?.label}
                    onDelete={() => handleFilterSelect(filter)}
                    sx={{
                      backgroundColor: alpha('#C6A75E', 0.1),
                      color: 'primary.main',
                      borderColor: 'primary.main',
                    }}
                  />
                ))}
                
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    color: '#616161',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" color="#616161">
            Showing {paginatedCoins.length} of {filteredCoins.length} synthetic assets
          </Typography>
          {filteredCoins.length > 0 && (
            <Typography variant="caption" color="#9E9E9E">
              Page {page} of {totalPages}
            </Typography>
          )}
        </Box>

        {/* Synthetic Coins Grid */}
        {filteredCoins.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedCoins.map((coin, index) => (
                <Grid item xs={12} md={6} lg={4} key={coin.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <SyntheticCard ispremium={coin.isPremium ? 1 : 0}>
                      {coin.isPremium && (
                        <PremiumBadge>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WorkspacePremiumIcon sx={{ fontSize: 12 }} />
                            PREMIUM
                          </Box>
                        </PremiumBadge>
                      )}
                      
                      <CardContent sx={{ p: 3 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              backgroundColor: alpha('#C6A75E', 0.1),
                              color: 'primary.main',
                              border: `2px solid ${coin.isPremium ? 'primary.main' : alpha('#9E9E9E', 0.2)}`,
                            }}
                          >
                            <DiamondIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                              {coin.name}
                            </Typography>
                            <Typography variant="body2" color="primary.main" sx={{ letterSpacing: '0.1em' }}>
                              {coin.symbol}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Price */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3 }}>
                          <Typography variant="h4" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                            ${coin.price.toLocaleString()}
                          </Typography>
                          <Chip
                            label={`${coin.change > 0 ? '+' : ''}${coin.change}%`}
                            size="small"
                            sx={{
                              backgroundColor: coin.change > 0 ? alpha('#4CAF50', 0.1) : alpha('#F44336', 0.1),
                              color: coin.change > 0 ? '#4CAF50' : '#F44336',
                              fontWeight: 600,
                            }}
                          />
                        </Box>

                        {/* Market Stats */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="#9E9E9E" display="block">
                              Market Cap
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 500 }}>
                              ${coin.marketCap}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="#9E9E9E" display="block">
                              Volume (24h)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 500 }}>
                              ${coin.volume}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="#9E9E9E" display="block">
                              Holders
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 500 }}>
                              {coin.holders.toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="#9E9E9E" display="block">
                              Category
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 500, textTransform: 'capitalize' }}>
                              {coin.category}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Features */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                          {coin.features.map((feature, i) => (
                            <Chip
                              key={i}
                              label={feature}
                              size="small"
                              sx={{
                                backgroundColor: alpha('#C6A75E', 0.08),
                                color: '#616161',
                                fontSize: '0.65rem',
                                height: 20,
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>

                      <Divider sx={{ borderColor: alpha('#9E9E9E', 0.15) }} />
                      
                      <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SwapHorizIcon />}
                          sx={{
                            borderColor: alpha('#9E9E9E', 0.3),
                            color: '#616161',
                            '&:hover': {
                              borderColor: 'primary.main',
                              color: 'primary.main',
                            },
                          }}
                        >
                          Trade
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ShowChartIcon />}
                          sx={{
                            backgroundColor: 'primary.main',
                            color: '#000000',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </SyntheticCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Load More / Pagination */}
            {paginatedCoins.length < filteredCoins.length ? (
              <Box ref={loadMoreRef} sx={{ textAlign: 'center', mt: 8 }}>
                {loading ? (
                  <Grid container spacing={3}>
                    {[1, 2, 3].map((n) => (
                      <Grid item xs={12} md={4} key={n}>
                        <Skeleton 
                          variant="rectangular" 
                          height={400} 
                          sx={{ 
                            backgroundColor: alpha('#9E9E9E', 0.1),
                          }} 
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => setPage(prev => prev + 1)}
                    endIcon={<ArrowUpwardIcon sx={{ transform: 'rotate(90deg)' }} />}
                    sx={{ 
                      px: 6, 
                      py: 2,
                      borderColor: alpha('#9E9E9E', 0.3),
                      color: '#616161',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: alpha('#C6A75E', 0.04),
                      },
                    }}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            ) : (
              paginatedCoins.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                  <Pagination 
                    count={totalPages} 
                    page={page}
                    onChange={(e, val) => {
                      setPage(val);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#616161',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )
            )}
          </>
        ) : (
          // No Results State
          <Fade in={true}>
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                border: '1px solid',
                borderColor: alpha('#9E9E9E', 0.15),
              }}
            >
              <PrecisionManufacturingIcon sx={{ fontSize: 64, color: alpha('#9E9E9E', 0.3), mb: 3 }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D' }}>
                No Synthetic Assets Found
              </Typography>
              <Typography variant="body2" color="#616161" sx={{ mb: 4 }}>
                Try adjusting your search or filters to find what you're looking for.
              </Typography>
              <Button
                variant="contained"
                onClick={handleClearFilters}
              >
                Clear All Filters
              </Button>
            </Paper>
          </Fade>
        )}

        {/* Filter Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              mt: 2,
              minWidth: 220,
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: 'primary.main',
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${alpha('#9E9E9E', 0.15)}` }}>
            <Typography variant="subtitle2" sx={{ color: '#2D2D2D' }}>
              Filter By
            </Typography>
          </Box>
          {filterOptions.map((option) => (
            <MenuItem 
              key={option.value}
              onClick={() => handleFilterSelect(option.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                backgroundColor: activeFilters.includes(option.value) ? alpha('#C6A75E', 0.08) : 'transparent',
                '&:hover': {
                  backgroundColor: alpha('#C6A75E', 0.04),
                },
              }}
            >
              <Box sx={{ color: activeFilters.includes(option.value) ? 'primary.main' : '#9E9E9E' }}>
                {option.icon}
              </Box>
              <Typography variant="body2" sx={{ flex: 1, color: '#2D2D2D' }}>
                {option.label}
              </Typography>
              {activeFilters.includes(option.value) && (
                <CheckIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Scroll to Top Button */}
        <Fade in={page > 1}>
          <Tooltip title="Scroll to top" arrow placement="left">
            <IconButton
              onClick={handleScrollToTop}
              sx={{
                position: 'fixed',
                bottom: 30,
                right: 30,
                backgroundColor: 'primary.main',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
              }}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Tooltip>
        </Fade>
      </Container>
    </Box>
  );
};

export default SyntheticCoins;